/**
 * Genre Hooks
 *
 * React Query hooks for fetching track genre/tag data.
 */

import { useQuery } from '@tanstack/react-query';
import { genreService } from '../services/genres';

/**
 * Query keys for genre-related queries
 * Hierarchical structure for efficient cache invalidation
 */
export const genreKeys = {
  all: ['genres'] as const,
  tracks: () => [...genreKeys.all, 'tracks'] as const,
  track: (trackId: string) => [...genreKeys.tracks(), trackId] as const,
  batch: (trackIds: string[]) => [
    ...genreKeys.tracks(),
    'batch',
    trackIds.sort().join(','),
  ] as const,
};

/**
 * Hook to get genres for a single track
 *
 * Fetches genre tags from Last.fm and MusicBrainz providers.
 * Data is cached indefinitely since genre data rarely changes.
 *
 * @param trackId - Spotify track ID
 * @returns Query result with track genres
 *
 * @example
 * ```tsx
 * function TrackGenres({ trackId }: Props) {
 *   const { data, isLoading, error } = useTrackGenres(trackId);
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <Error />;
 *
 *   return (
 *     <div>
 *       {data.genres.map(genre => (
 *         <Badge key={genre.name}>{genre.name}</Badge>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useTrackGenres(trackId: string | undefined) {
  return useQuery({
    queryKey: genreKeys.track(trackId!),
    queryFn: () => genreService.getTrackGenres(trackId!),
    enabled: !!trackId,
    staleTime: Infinity, // Genre data rarely changes
    retry: 1, // Don't retry aggressively for genre data
  });
}

/**
 * Hook to get genres for multiple tracks in batch
 *
 * Fetches genres for up to 50 tracks. Failed tracks are skipped silently.
 * Data is cached indefinitely since genre data rarely changes.
 *
 * @param trackIds - Array of Spotify track IDs (max 50)
 * @param enabled - Whether to run the query (default: true)
 * @returns Query result with batch genre response
 *
 * @example
 * ```tsx
 * function PlaylistGenres({ trackIds }: Props) {
 *   const { data, isLoading } = useBatchTrackGenres(trackIds);
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       <p>Fetched {data.totalReturned} of {data.totalRequested} tracks</p>
 *       {data.tracks.map(track => (
 *         <TrackGenres key={track.trackId} genres={track.genres} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useBatchTrackGenres(trackIds: string[], enabled: boolean = true) {
  return useQuery({
    queryKey: genreKeys.batch(trackIds),
    queryFn: () => genreService.getTrackGenresBatch(trackIds),
    enabled: enabled && trackIds.length > 0,
    staleTime: Infinity, // Genre data rarely changes
    retry: 1, // Don't retry aggressively for genre data
  });
}
